import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useInView from '@propertypal/shared/src/hooks/useInView';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import { MarketTrendsResponse } from '@propertypal/shared/src/types/marketTrends';
import { roundOff } from '@propertypal/shared/src/utils/number';
import { getFormattedPrice } from '@propertypal/shared/src/utils/property';
import dynamic from 'next/dynamic';
import React, { FunctionComponent, useRef } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import useMediaQuery from '../media-query/useMediaQuery';
import { SubHeading, Text } from '../typography';
import AreaPriceHistory from './AreaPriceHistory';
import {
  LoadingBox,
  NiMap,
  Wrapper,
  Content,
  MapContent,
  StatsContent,
  StyleStats,
  CouncilBox,
  AreaBox,
  AreaBoxProps,
  AreaMap,
} from './MarketTrends.style';

const ArmaghAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/abc-electoral-area-map.svg'),
);
const AntrimAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/an-electoral-areas-map.svg'),
);
const ArdsAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/and-electoral-areas-map.svg'),
);
const BelfastAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/bcc-electoral-areas-map.svg'),
);
const CausewayAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/ccg-electoral-areas-map.svg'),
);
const DerryAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/dscdc-electoral-areas-map.svg'),
);
const FermanaghAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/fo-electoral-areas-map.svg'),
);
const LisburnAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/lcc-electoral-areas-map.svg'),
);
const MidEastAntrimAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/mea-electoral-areas-map.svg'),
);
const MidUlsterAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/mu-electoral-areas-map.svg'),
);
const NewryAreaMap = dynamic(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/nmd-electoral-areas-map.svg'),
);

interface Props {
  marketTrendsData?: MarketTrendsResponse;
  loading?: boolean;
  onInView?: () => void;
  error?: any;
  addressLine1: string;
}

const BOX_AREA_PROPS: Record<string, AreaBoxProps> = {
  N09000001: {
    bottomTab: -20,
    maxW: 220,
  },
  N09000002: {
    bottomTab: -40,
    maxW: 250,
  },
  N09000003: {
    bottom: 60,
    right: 60,
    bottomTab: -30,
  },
  N09000004: {
    bottom: 60,
    right: 60,
    maxW: 220,
    bottomTab: -30,
    rightTab: 20,
  },
  N09000005: {
    right: 20,
    maxW: 200,
    bottomTab: -30,
  },
  N09000006: {
    bottomTab: -50,
    maxW: 200,
  },
  N09000007: {
    right: 20,
    bottomTab: -50,
    maxW: 200,
  },
  N09000008: {
    leftMob: 0,
    top: 10,
    topTab: -30,
    maxW: 200,
    bottomMob: 0,
    right: 40,
  },
  N09000009: {
    right: 10,
    bottomTab: -40,
    maxW: 200,
  },
  N09000010: {
    right: 20,
    maxW: 200,
    bottomTab: -40,
  },
  N09000011: {
    right: '30%',
    rightTab: 100,
    maxW: 200,
  },
};

const MarketTrends: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const container = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  const windowSize = useWindowSize();
  useInView(container, { threshold: 0.5, rootMargin: '0px 0px 800px' }, props.onInView);

  if (windowSize.width === undefined || props.loading)
    return (
      <LoadingBox ref={container} data-testid="marketTrendsLoading">
        <Loader color={theme.backgroundLight} />
        <Text mt={15}>Loading Northern Ireland House Price Data</Text>
      </LoadingBox>
    );

  if (props.error || !props.marketTrendsData)
    return (
      <LoadingBox data-testid="market-trends-error" bg={theme.backgroundLighter}>
        <Text mt={15}>Northern Ireland House Prices Data Unavailable</Text>
      </LoadingBox>
    );

  const { marketTrendsData } = props;

  const electoralArea = marketTrendsData.data.find((p) => p.level === 'ELECTORAL_AREA')?.label;
  const councilArea = `${marketTrendsData.data.find((p) => p.level === 'COUCIL_AREA')?.label} Council`;

  return (
    <>
      <Wrapper data-testid="market-trends-data">
        {!isMobile && (
          <SubHeading data-testid="market-trends-heading" mt={60} mb={30}>
            Northern Ireland House Prices
          </SubHeading>
        )}

        <Text fontSize={18} w={!isMobile ? 'calc(100% - 350px)' : undefined} mt={isMobile ? 30 : undefined}>
          Whether you&apos;re buying, renting or just exploring your options, understanding the local market is crucial
          to making the right choice. Our latest market data and average prices give you the insights needed to make
          confident, informed property decisions.
        </Text>

        <Text
          data-testid="market-trends-details"
          fontSize={24}
          w={!isMobile ? 'calc(100% - 350px)' : undefined}
          color={theme.primary}
          mt={35}
          mb={45}
          align="center"
        >
          {props.addressLine1} is located in the {electoralArea} area of {councilArea}
        </Text>

        <Content data-testid="marketTrends">
          <MapContent>
            <CouncilBox>
              <Text fontWeight="bold" align="center" fontSize={14}>
                Northern Ireland Councils
              </Text>
            </CouncilBox>
            <NiMap district={marketTrendsData?.coucilArea?.id} />

            <AreaMap id={marketTrendsData?.coucilArea?.id}>
              {marketTrendsData?.coucilArea?.id === 'N09000001' && <AntrimAreaMap data-testid="antrim-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000002' && <ArmaghAreaMap data-testid="armagh-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000003' && <BelfastAreaMap data-testid="belfast-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000004' && <CausewayAreaMap data-testid="causeway-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000005' && <DerryAreaMap data-testid="derry-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000006' && <FermanaghAreaMap data-testid="fermanagh-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000007' && <LisburnAreaMap data-testid="lisburn-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000008' && (
                <MidEastAntrimAreaMap data-testid="mid-east-antrim-map" />
              )}
              {marketTrendsData?.coucilArea?.id === 'N09000009' && <MidUlsterAreaMap data-testid="mid-ulster-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000010' && <NewryAreaMap data-testid="newry-map" />}
              {marketTrendsData?.coucilArea?.id === 'N09000011' && <ArdsAreaMap data-testid="ards-map" />}
            </AreaMap>

            <AreaBox
              {...((marketTrendsData?.coucilArea?.id && BOX_AREA_PROPS?.[marketTrendsData?.coucilArea?.id]) || {})}
            >
              <Text fontWeight="bold" align="center" fontSize={14}>
                {councilArea} Electoral Areas
              </Text>
            </AreaBox>
          </MapContent>
          <StatsContent>
            <StyleStats>
              <div>
                <Text mb={5}>This property&apos;s electoral area is</Text>
                <Text fontSize={isMobile ? 24 : 30} color="#36a3dc">
                  {electoralArea}
                </Text>
              </div>
            </StyleStats>
            <StyleStats>
              <div>
                <Text mb={5}>{electoralArea} is located in</Text>
                <Text fontSize={isMobile ? 24 : 30} color="#2a5186">
                  {councilArea}
                </Text>
              </div>
            </StyleStats>
            {marketTrendsData.averageAreaPrice && (
              <StyleStats data-testid="avg-area-price">
                <div>
                  <Text mb={5}>
                    Average price in {marketTrendsData.averageAreaPrice.term}: {marketTrendsData.averageAreaPrice.label}
                  </Text>
                  <Text fontSize={isMobile ? 24 : 30} color="#2a5186">
                    {getFormattedPrice(roundOff(marketTrendsData?.averageAreaPrice?.value || 0, 0), '£')}
                  </Text>
                </div>
              </StyleStats>
            )}

            {marketTrendsData.averageAreaPriceGrowth?.change && (
              <StyleStats data-testid="avg-area-price">
                <div>
                  <Text mb={5}>
                    Average price growth in {marketTrendsData.averageAreaPriceGrowth.term}:{' '}
                    {marketTrendsData.averageAreaPriceGrowth.label}
                  </Text>
                  <Text fontSize={isMobile ? 24 : 30} color="#2a5186">
                    {roundOff(marketTrendsData.averageAreaPriceGrowth.change * 100, 1)}%
                  </Text>
                </div>
              </StyleStats>
            )}
          </StatsContent>
        </Content>
        <AreaPriceHistory data={marketTrendsData} />
      </Wrapper>
    </>
  );
};

export default MarketTrends;
